import React, { useEffect } from "react";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { getSiteUrl, getSiteEmail } from "../Helpers/SiteConfig";
import usePageMeta from "../Helpers/usePageMeta";

const TermsAndConditions = () => {
  usePageMeta({
    title: "Terms & Conditions | TZ Creation",
    description:
      "Terms and conditions for using TZ Creation's website and services.",
    path: "/terms-and-conditions",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Header />
      <Breadcrumb
        parentpageName="Home"
        currentPageName="Terms and Conditions"
      />
      <section className="text-dark tnc_section section-gap">
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <h1 className="text-uppercase mb-4">Terms & Conditions</h1>
              <h2 className="mt-5 mb-2">TERMS OF ENGAGEMENT</h2>
              <p>
                By engaging with The TZ Creation and entering into a working
                relationship with them, you are hereby accepting and agreeing to
                the following terms of service.
              </p>
              <h2 className="mt-5 mb-2">1. RIGHT TO USE SERVICES</h2>
              <div className="pl-16">
                <h3 className="mt-5 mb-2">1.1 Use of Services</h3>
                <p>
                  When using our Services, you must adhere to the designated use
                  levels that we determine, price, and offer on our websites,
                  your Order, or the Service Descriptions ("Use Levels"). These
                  Services may only be used in accordance with these Terms, and
                  you must acknowledge our&nbsp;
                  <a href={`${getSiteUrl()}/privacy-policy`}>
                    Privacy Policy
                  </a>
                  , which is incorporated by reference. We grant you a limited
                  right to use our Services solely for business and professional
                  purposes. Technical support details for the Services can be
                  found in the Service Descriptions. If your affiliates utilize
                  our Services, you are responsible for ensuring their
                  compliance with the Agreement and will be held liable for any
                  non-compliance. "Service(s)" refers to our
                  software-as-a-service offerings and audio services, including
                  any associated hardware, provided by TZ Creation, or their
                  subsidiaries, along with the telecommunications providers
                  responsible for the respective audio services' rates and
                  terms. The Service Descriptions form part of these Terms.
                  Please note that your personal data may be processed when
                  using our Services, software, and websites, as they are hosted
                  on equipment and resources located worldwide.
                </p>
                <h3 className="mt-5 mb-2">1.2 Limitations on Use</h3>
                <p className="mb-1 font-w600">
                  By utilizing our Services, you agree, on behalf of yourself,
                  your users, and your attendees, to adhere to the following
                  restrictions:
                </p>
                <ul>
                  <li>
                    Do not modify, create derivative works of, or reverse
                    engineer our Services.
                  </li>
                  <li>
                    Do not engage in intentional or negligent behavior that
                    abuses or disrupts our networks, user accounts, or the
                    Services.
                  </li>
                  <li>
                    Do not transmit any harassing, indecent, obscene, or
                    unlawful material through the Services.
                  </li>
                  <li>
                    Do not market or resell the Services to any third party.
                  </li>
                  <li>
                    Do not violate applicable laws or regulations when using the
                    Services.
                  </li>
                  <li>
                    Do not use the Services for unauthorized advertising or
                    spam.
                  </li>
                  <li>
                    Do not harvest, collect, or gather user data without proper
                    consent.
                  </li>
                  <li>
                    Do not transmit any material through the Services that may
                    infringe upon the intellectual property, privacy, or other
                    rights of third parties.
                  </li>
                  <li>
                    Do not utilize the Services to commit fraud or impersonate
                    any person or entity.
                  </li>
                </ul>
                <h3 className="mt-5 mb-2">1.3 Changes to Services</h3>
                <p>
                  We reserve the right to enhance, upgrade, improve, or modify
                  features of our Services as we deem appropriate and at our
                  sole discretion. We may also provide additional functionality
                  or premium feature improvements for an additional cost beyond
                  our standard Services.
                </p>
                <h3 className="mt-5 mb-2">1.4 Proprietary Rights</h3>
                <p>
                  You acknowledge and agree that we, or our licensors, retain
                  all proprietary rights, title, and interest in the Services,
                  including our name, logo, and other marks, along with any
                  associated intellectual property rights, including
                  modifications, enhancements, derivative works, and upgrades.
                  You are prohibited from using or registering any trademark,
                  service mark, business name, domain name, or social media
                  account name or handle that incorporates the TZ Creation name
                  in whole or in part, or any name that is similar to these. You
                  are also required to comply with our Branding Guidelines,
                  which are incorporated into this Agreement by reference.
                </p>
              </div>
              <h2 className="mt-5 mb-2">2. ORDERS, FEES AND PAYMENT</h2>
              <div className="pl-16">
                <h3 className="mt-5 mb-2">2.1 Orders</h3>
                <p>
                  You can place orders for our Services using our current
                  ordering processes ("Order"). All Orders become effective on
                  either (i) the date you submit your Order, or (ii) the date
                  indicated on the signature block of the Order ("Effective
                  Date"). Acceptance of your Order may be subject to
                  verification and credit approval by us. Each Order is
                  considered separate and independent. For non-credit card
                  transactions over 25,000 USD or equivalent, a Purchase Order
                  is required unless it is not part of your purchasing process.
                </p>
                <h3 className="mt-5 mb-2">2.2 Fees and Payment</h3>
                <p>
                  You are responsible for paying all applicable and undisputed
                  fees for the Services as specified in this Agreement or your
                  invoice. Unless otherwise stated in Section 3.3 or the Service
                  Descriptions, all payments made for accessing the Services are
                  final and non-refundable. You are also responsible for any
                  fees or charges incurred with other service providers (e.g.,
                  broadband and internet providers) related to your use of the
                  Services. It is your responsibility to provide accurate and
                  up-to-date billing, contact, and payment information to us or
                  any reseller. By using the Services, you authorize us to
                  charge your payment card or bill you for all amounts owed. We
                  may update your payment card information (where permitted) to
                  ensure successful processing. Your credit card information and
                  related personal data may be shared with third parties for
                  payment processing and fraud prevention purposes. If we
                  determine that your payment information is inaccurate or not
                  current, we may suspend or terminate your Services, and you
                  will be responsible for any fees and overdraft charges
                  incurred. We do not accept invoice submissions through
                  customer procure-to-pay online portals or Electronic Data
                  Interchange (EDI) portals. We reserve the right to change the
                  price for Services at any time after the Initial Term, and
                  such changes will be effective in your next billing cycle. We
                  will provide timely notice of any price changes in accordance
                  with applicable law by publishing on our website, emailing,
                  quoting, or invoicing you.
                </p>
                <h3 className="mt-5 mb-2">
                  2.3 Sales, Promotional Offers, Coupons and Pricing
                </h3>
                <p>
                  Sales, promotions, and other special discounted pricing offers
                  are temporary, and upon subscription renewal, any such
                  discounts may expire. We reserve the right to discontinue or
                  modify any coupons, credits, sales, or special promotional
                  offers at our sole discretion.
                </p>
                <h3 className="mt-5 mb-2">2.4 Disputes; Delinquent Accounts</h3>
                <p>
                  If you have a fee dispute, you must notify us within 15 days
                  of the invoice date, and once resolved, you agree to make
                  payment within 15 days. Failure to pay undisputed fees may
                  result in the suspension or termination of your Services, and
                  you agree to reimburse us for reasonable costs and expenses
                  incurred in collecting any undisputed delinquent amounts.
                </p>
                <h3 className="mt-5 mb-2">2.5 Taxes and Withholding</h3>
                <p>
                  You are responsible for all applicable Taxes and Fees imposed
                  by any government entity or collecting agency based on the
                  Services, including sales, services, value-added, goods and
                  services, withholding, tariffs, Universal Services Fund (USF)
                  fees, or similar fees (if applicable to Audio Services only).
                  However, Taxes and Fees based on our net income or for which
                  you have provided an exemption certificate are not your
                  responsibility. In all cases, you must pay the amounts due
                  under this Agreement to us in full without any right of
                  set-off or deduction.
                </p>
              </div>
              <h2 className="mt-5 mb-2">3. TERM AND TERMINATION</h2>
              <div className="pl-16">
                <h3 className="mt-5 mb-2">3.1 Term</h3>
                <p>
                  The initial term commitment for your purchase of Services will
                  be as specified on an Order ("Initial Term") and begins on the
                  Effective Date. After the Initial Term, the Services will,
                  unless otherwise specified in the Service Description for a
                  particular Service, automatically renew for additional
                  12-month periods ("Renewal Terms"), unless either party
                  provides notice of non-renewal at least 30 days before the
                  current term expires. You may provide notice of non-renewal
                  for each Service you do not wish to renew at&nbsp;
                  <a href={`mailto:${getSiteEmail()}`}>{getSiteEmail()}</a>. We
                  may agree to align the invoicing under multiple Orders, but
                  this will not reduce the term of any Order. Terminating
                  specific Services does not affect the term of any other
                  Services still in effect. If we permit you to reinstate
                  Services at any time after termination, you agree that you
                  will be bound by the then-current Terms and the renewal date
                  that was in effect as of the effective termination date.
                </p>
                <h3 className="mt-5 mb-2">3.2 Termination for Cause</h3>
                <p>
                  Either party may terminate the Agreement in the following
                  circumstances: (i) if the other party breaches its material
                  obligations and fails to cure within 30 days of receipt of
                  written notice, or (ii) where permitted by applicable law, if
                  the other party becomes insolvent or bankrupt, liquidated or
                  is dissolved, or ceases substantially all of its business.
                  Additionally, we may suspend access or terminate immediately
                  if you breach Section 1.2, 4.1, 4.3, or 5.
                </p>
                <h3 className="mt-5 mb-2">3.3 Effect of Termination</h3>
                <p>
                  If the Agreement or any Services are terminated, your account
                  may be converted to a "free" or "basic" version of the
                  Service, if available, at our discretion. Otherwise, you will
                  immediately discontinue all use of the terminated Services,
                  except that upon request, we will provide you with limited
                  access to the Services for a period not exceeding 30 days,
                  solely to enable you to retrieve your Content from the
                  Services. We have no obligation to maintain your Content after
                  that period. To the extent permitted by applicable law,
                  neither party will be liable for any damages resulting from
                  termination of the Agreement, and termination will not affect
                  any claim arising prior to the effective termination date. You
                  agree to pay for any use of the Services past the date of
                  expiration or termination that has not been converted to a
                  free version of the Service.
                </p>
                <h3 className="mt-5 mb-2">3.4 Survival</h3>
                <p>
                  The provisions of Sections 2 (Orders, Fees and Payment), 3.3
                  (Effect of Termination), 4 (Your Content and Accounts), 7
                  (Indemnification), 8 (Limitation on Liability), and 9.8
                  (Contracting Party, Choice of Law and Location for Resolving
                  Disputes) will survive any termination of the Agreement.
                </p>
              </div>
              <h2 className="mt-5 mb-2">4. YOUR CONTENT AND ACCOUNTS</h2>
              <div className="pl-16">
                <h3 className="mt-5 mb-2">4.1 Your Content</h3>
                <p>
                  You retain all rights to your Content (defined below), and we
                  do not own or license your Content. In order to provide and
                  operate the Services, we may use, modify, reproduce, and
                  distribute your Content. By uploading or sharing Content with
                  us, you warrant that you have the right to do so and that it
                  does not infringe on any third-party rights. Both parties
                  agree to implement reasonable technical, organizational, and
                  administrative security measures to protect the Content in
                  accordance with industry standards. We will not access, view,
                  or process your Content except as authorized by you or as
                  required to comply with our policies, applicable law, or
                  governmental requests. "Content" refers to files, documents,
                  recordings, chat logs, transcripts, and similar data
                  maintained on your or your users' behalf, as well as any other
                  information uploaded to your Service account in connection
                  with the Services.
                </p>
                <h3 className="mt-5 mb-2">4.2 Your Privacy and Security</h3>
                <p>
                  We maintain a global privacy and security program to protect
                  your Content and any associated personal data collected or
                  processed on your behalf.
                </p>
                <h3 className="mt-5 mb-2">4.3 Your Accounts</h3>
                <p>
                  You are solely responsible for the use of the Services by you
                  and your users, maintaining a lawful basis for the collection,
                  use, processing, and transfer of Content, and providing
                  necessary notices or obtaining consent as legally required.
                  Please keep your account information, including usernames and
                  passwords, confidential to ensure account security. We will
                  not send emails requesting your account credentials. We are
                  not liable for any loss you may incur if a third party uses
                  your password or account. We may suspend the Services or
                  terminate the Agreement if we have reasonable grounds to
                  suspect illegal, fraudulent, or abusive activity, or if your
                  use of the Services is likely to cause harm to us. Please
                  notify us immediately of any unauthorized access or security
                  breaches and terminate such access.
                </p>
              </div>
              <h2 className="mt-5 mb-2">5. COMPLIANCE WITH LAWS</h2>
              <div className="pl-16">
                <p>
                  In connection with the performance, access, and use of the
                  Services under the Agreement, each party agrees to comply with
                  all applicable laws, rules, and regulations, including export,
                  privacy, and data protection laws and regulations. Each party
                  represents that it is not named on any government denied-party
                  list. If necessary and in accordance with applicable law, we
                  will cooperate with local, state, and federal government
                  authorities in India. Notwithstanding any other provision in
                  these Terms, we may immediately terminate the Agreement for
                  noncompliance with applicable laws.
                </p>
              </div>
              <h2 className="mt-5 mb-2">6. WARRANTIES</h2>
              <div className="pl-16">
                <p className="mb-1 font-w600">
                  We warrant that the Services will conform to the Service
                  Descriptions under normal use. However, we do not represent or
                  warrant that:
                </p>
                <ol>
                  <li>
                    The use of our Services will be timely, uninterrupted, or
                    error-free, or operate in combination with any specific
                    hardware, software, system, or data.
                  </li>
                  <li>Our Services will meet your specific requirements.</li>
                  <li>All errors or defects will be corrected.</li>
                </ol>
                <p className="mt-4 mb-1 font-w600">
                  It's important to note that the use of the Services is at your
                  sole risk. Our entire liability and your exclusive remedy
                  under this warranty will be, at our sole option and subject to
                  applicable law:
                </p>
                <ol>
                  <li>To provide conforming Services; or</li>
                  <li>
                    To terminate the non-conforming Services or the applicable
                    Order and provide a pro-rated refund of any prepaid fees
                    from the date you notify us of the non-conformance through
                    the end of the remaining term.
                  </li>
                </ol>
                <p className="mt-4">
                  To the extent permitted by applicable law, we disclaim all
                  other warranties and conditions, whether express, implied,
                  statutory, or otherwise, including any express or implied
                  warranties of merchantability, satisfactory quality, title,
                  fitness for a particular purpose, and non-infringement. Please
                  be aware that some jurisdictions do not allow the exclusion of
                  certain warranties and conditions, so some of the above
                  exclusions may not apply to customers located in those
                  jurisdictions.
                </p>
                <p>
                  We strive to provide reliable and high-quality services, but
                  it's important to acknowledge that there may be limitations
                  and potential issues that could arise. If you encounter any
                  non-conformance or have concerns about the Services, please
                  notify us, and we will work towards resolving the issue in
                  accordance with the terms outlined above.
                </p>
              </div>
              <h2 className="mt-5 mb-2">7. INDEMNIFICATION</h2>
              <div className="pl-16">
                <p className="mb-1 font-w600">
                  You agree to indemnify and defend us against any third-party
                  claim that arises as a result of:
                </p>
                <ol>
                  <li>
                    A breach of Section 1.2, 4.1, or 4.3 of these terms; or
                  </li>
                  <li>
                    Allegations that any of your Content infringes upon any
                    patent, copyright, or violates a trade secret of any party.
                  </li>
                </ol>
                <p className="mt-4">
                  In the event of such a claim, you agree to pay reasonable
                  attorney's fees, court costs, damages finally awarded, or
                  reasonable settlement costs. We will promptly notify you of
                  any claim and cooperate with you in defending the claim.
                  Additionally, you agree to reimburse us for any reasonable
                  expenses incurred in providing cooperation or assistance.
                </p>
                <p className="mb-1 font-w600">
                  You will have full control and authority over the defense and
                  settlement of any claim, except in two specific circumstances:
                </p>
                <ol>
                  <li>
                    Any settlement requiring us to admit liability requires
                    prior written consent, which will not be unreasonably
                    withheld or delayed.
                  </li>
                  <li>
                    We may join in the defense with our own counsel at our own
                    expense.
                  </li>
                </ol>
                <p>
                  It's important to note that this indemnification provision
                  serves to protect both parties involved and ensures that any
                  third-party claims are addressed and resolved appropriately.
                </p>
              </div>

              <h2 className="mt-5 mb-2">8. LIMITATION ON LIABILITY</h2>
              <div className="pl-16">
                <h3 className="mt-5 mb-2">
                  8.1. LIMITATION ON INDIRECT LIABILITY
                </h3>
                <p className="mb-1 font-w600">
                  Neither party will be liable to the other party or any other
                  person for any indirect, special, consequential, or incidental
                  loss, exemplary, or other similar damages. This includes
                  damages arising out of or relating to:
                </p>
                <ol>
                  <li>Loss of data</li>
                  <li>Loss of income</li>
                  <li>Loss of opportunity</li>
                  <li>Lost profits</li>
                  <li>Costs of recovery</li>
                </ol>
                <p>
                  These limitations apply regardless of the theory of liability,
                  whether it be breach of contract, tort (including negligence),
                  or violation of statute. The parties are aware that such
                  damages are possible and agree that neither party will be held
                  liable for them. However, please note that some jurisdictions
                  may not allow the limitation or exclusion of liability for
                  incidental or consequential damages, so some of the above
                  limitations may not apply.
                </p>
                <h3 className="mt-5 mb-2">
                  8.2. LIMITATION ON AMOUNT OF LIABILITY
                </h3>
                <p>
                  Except for your breach of Sections 1.2, 4.1, or 4.3 and your
                  indemnification obligations, and to the extent permitted by
                  applicable law, the total cumulative liability of either party
                  and their respective licensors and suppliers under this
                  Agreement is limited to the sum of the amounts paid for the
                  applicable service during the 12 months immediately preceding
                  the incident giving rise to the liability. It's important to
                  note that this limitation does not affect your obligations to
                  pay any undisputed fees and other amounts due under any order.
                </p>
                <p>
                  This limitation on liability ensures that neither party will
                  be held accountable for damages beyond a certain amount,
                  providing clarity and protection for both parties involved.
                </p>
              </div>

              <h2 className="mt-5 mb-2">9. ADDITIONAL TERMS</h2>
              <div className="pl-16">
                <h3 className="mt-5 mb-2">9.1. Free Services and Trials</h3>
                <p>
                  Your access to and use of any free Services is not guaranteed
                  for any specific period of time, and we reserve the right to
                  limit or terminate your use of any free versions of our
                  Services at our sole discretion. If you are using the Services
                  on a trial or promotional basis ("Trial Period"), your access
                  to the Services will terminate in one of the following ways:
                </p>
                <ol>
                  <li>At the end of the Trial Period stated in your Order.</li>
                  <li>
                    If no specific date is specified, 30 days after your initial
                    access to the Services.
                  </li>
                  <li>Upon your conversion to a subscription.</li>
                </ol>
                <p>
                  During the Trial Period, we provide the Services "AS IS" and
                  without warranty or indemnity, subject to all other applicable
                  terms. We reserve the right to modify or discontinue any
                  trials or promotions at any time without prior notice.
                </p>
                <h3 className="mt-5 mb-2">9.2. Third-Party Services</h3>
                <div className="pl-16">
                  <h4 className="mb-2">9.2.1. Linking and Integration</h4>
                  <p>
                    Our Services may provide the capability for you to link to
                    or integrate with third-party sites or applications that are
                    separately accessed by you and not purchased from us. It's
                    important to note that we are not responsible for and do not
                    endorse such services. The decision to purchase or connect
                    to any third-party services is solely at your discretion,
                    and your use of those services is governed solely by the
                    terms for those services.
                  </p>
                  <h4 className="mt-5 mb-2">
                    9.2.2. Third-Party Services We Have Sold to You
                  </h4>
                  <p>
                    Any third-party services that we have sold to you are
                    subject to this Agreement, including any additional terms
                    specific to those services as set forth in the Service
                    Descriptions. Unless otherwise specified, we and our
                    contractors, suppliers, and licensors disclaim all
                    warranties, express or implied, and all liability for any
                    third-party services that we have sold to you.
                  </p>
                </div>
                <h3 className="mt-5 mb-2">9.3. Beta Services</h3>
                <p>
                  We may offer you access to beta services ("Beta Services")
                  that are provided prior to general release. Please note that
                  we do not guarantee that these services will be made generally
                  available. By using the Beta Services, you understand and
                  agree that they may contain bugs, errors, and other defects,
                  and your use of the Beta Services is at your sole risk. You
                  acknowledge that your use of Beta Services is voluntary and
                  optional, and we have no obligation to provide technical
                  support or continue offering Beta Services. The Beta Services
                  are provided "AS-IS," and to the extent permitted by
                  applicable law, we disclaim any liability, warranties,
                  indemnities, and conditions, whether express, implied,
                  statutory, or otherwise. If you provide any feedback
                  ("Feedback") about the Beta Services, you agree that we own
                  any Feedback you share with us. These Terms supersede any
                  conflicting terms and conditions in the Agreement, but only to
                  the extent necessary to resolve the conflict, specifically for
                  the Beta Services.
                </p>
                <h3 className="mt-5 mb-2">9.4. Security Emergencies</h3>
                <p>
                  In the event that we reasonably determine that the security of
                  our Services or infrastructure may be compromised due to
                  hacking attempts, denial of service attacks, or other
                  malicious activities, we may temporarily suspend the Services
                  to resolve the security issues. We will notify you of any
                  suspension or other security-related actions taken.
                </p>
                <h3 className="mt-5 mb-2">9.5. High-Risk Use</h3>
                <p>
                  You acknowledge that the Services are not designed or intended
                  for use during high-risk activities, including but not limited
                  to hazardous environments requiring fail-safe controls,
                  weapons systems, aircraft navigation, control or communication
                  systems, and/or life support systems.
                </p>
                <h3 className="mt-5 mb-2">9.6. Recording</h3>
                <p>
                  Certain Services may offer functionality that allows you to
                  record audio/video/images and data shared during sessions. You
                  are solely responsible for complying with all applicable laws
                  in the relevant jurisdictions while using the recording
                  functionality. We disclaim all liability for your recording of
                  media or shared data, and you agree to hold us harmless from
                  any damages or liabilities related to the recording of
                  audio/video/images or any other data.
                </p>
                <h3 className="mt-5 mb-2">9.7. Assignment</h3>
                <p>
                  Neither party may assign its rights or delegate its duties
                  under the Agreement, except with the other party's prior
                  written consent. However, either party may assign the
                  Agreement to an affiliated entity or as part of a corporate
                  reorganization, consolidation, merger, acquisition, or sale of
                  all or substantially all of its business or assets related to
                  this Agreement. Any attempted assignment without consent will
                  be void. The Agreement will bind and benefit each party's
                  successors or assigns.
                </p>
                <h3 className="mt-5 mb-2">
                  9.8. Contracting Party, Choice of Law and Location for
                  Resolving Disputes
                </h3>
                <p>
                  The specific TZ Creation contracting entity, contact
                  information, and governing law for your use of the Services
                  will depend on the specific Services you have ordered.
                </p>
                <h2 className="mt-5 mb-2">GENERAL TERMS</h2>
                <h2 className="mb-2">1. PROVISIONS IN THE AGREEMENT</h2>
                <p>
                  If any term of this Agreement is found to be unenforceable, it
                  will not affect the enforceability of any other terms. The
                  parties to this Agreement are independent contractors, and
                  nothing in this Agreement creates a partnership, agency,
                  fiduciary, or employment relationship between them. No person
                  or entity not a party to the Agreement will be considered a
                  third-party beneficiary. Our authorized distributors do not
                  have the authority to modify the Agreement or make commitments
                  binding on us. Failure to enforce any right under the
                  Agreement will not waive that right. Unless otherwise
                  specified, remedies are cumulative. The Agreement may be
                  agreed to online or executed by electronic signature in one or
                  more counterparts. No party will be responsible for any delay
                  or failure to perform under the Agreement due to force majeure
                  events, such as natural disasters, activities of third-party
                  service providers, labor disputes, or acts of government, as
                  long as those conditions persist.
                </p>
                <h2 className="mt-5 mb-2">2. OUR REVISION POLICY</h2>
                <p>
                  At our company, we strive to provide the best services to our
                  customers. We understand the importance of revisions to ensure
                  customer satisfaction. The number of revisions included in
                  your package is clearly stated in your package details. You
                  are entitled to request the specified number of revisions
                  without any additional charges.
                </p>
                <p>
                  If the design concepts remain unchanged or the code remains
                  the same, you will not be required to pay any extra fees for
                  revisions. We aim to deliver your revised design or code
                  within 48 hours of receiving your revision request.
                </p>
                <h2 className="mt-5 mb-2">3. TURNAROUND TIME</h2>
                <p>
                  At TZ Creation, we strive to provide efficient and timely
                  design services to our clients. Our initial turnaround time
                  for most design services is 48-72 hours. Please note that this
                  timeframe refers to a single iteration of a design or
                  development stage, such as a Graphic Design, Ui/Ux Design,
                  Website or App.
                </p>
                <p>
                  Depending on the specific service and the requested revisions,
                  the turnaround time for revisions can range from a minimum of
                  48 hours to 72 hours. This timeframe corresponds to 2-3
                  working days.
                </p>
                <p className="mb-1 font-w600">
                  To provide a clearer understanding, here's an example:
                </p>
                <ul>
                  <li>
                    Orders placed or revision requests made on a Monday will be
                    delivered by Wednesday.
                  </li>
                </ul>
                <p>
                  We value your time and aim to deliver your designs promptly to
                  meet your project requirements.
                </p>
                <h2 className="mt-5 mb-2">4. 100% MONEY BACK GUARANTEE</h2>
                <p>
                  At TZ Creation, we are committed to customer satisfaction and
                  stand behind the quality of our design services. We offer a
                  100% Money Back Guarantee to ensure that our customers feel
                  confident and protected when working with us. You can claim a
                  full refund of the amount paid for the service under certain
                  circumstances as outlined in our&nbsp;
                  <a
                    href={`${getSiteUrl()}/refund-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Refund Policy
                  </a>
                  .
                </p>
                <h2 className="mt-5 mb-2">5. DELIVERY POLICY</h2>
                <p>
                  At TZ Creation, we strive to provide a seamless and efficient
                  delivery process to ensure that you receive your order in a
                  timely manner. Our Delivery Policy outlines the key aspects of
                  our delivery process and your responsibilities as a customer.
                </p>
                <p className="mb-1 font-w600">
                  Please take note of the following points regarding our
                  Delivery Policy:
                </p>
                <ol>
                  <li>
                    <span className="font-w600">Delivery Method:</span> The
                    complete order will be delivered to the account specified
                    during the ordering process. Additionally, a confirmation
                    email will be sent to the provided email address to
                    acknowledge the successful delivery of your order.
                  </li>
                  <li>
                    <span className="font-w600">Turnaround Time:</span> The
                    turnaround time for your order will be determined by the
                    package you have selected. The minimum time required for
                    delivery is 2 business days. Please refer to your Order
                    Confirmation for the specific date of delivery.
                  </li>
                  <li>
                    <span className="font-w600">Website Activation:</span> For
                    website orders, please note that the website will only be
                    made live once all outstanding dues have been cleared. We
                    require full payment before activating and making the
                    website accessible to the public.
                  </li>
                  <li>
                    <span className="font-w600">Urgent or Rush Orders:</span> If
                    you require an urgent or rush order, please get in touch
                    with our customer support team. They will assist you in
                    expediting the process and accommodating your specific
                    requirements, subject to feasibility and availability.
                  </li>
                </ol>
                <h2 className="mt-5 mb-2">6. RIGHTS & OWNERSHIP</h2>
                <p>
                  At TZ Creation, we believe in empowering our clients with
                  complete ownership of the finalized designs we provide.
                </p>
                <p className="mb-1 font-w600">
                  Please review the following key points regarding our Content
                  Rights & Ownership policy:
                </p>
                <ol>
                  <li>
                    <span className="font-w600">Ownership of product: </span>
                    Upon written request for finalization by the client, TZ
                    Creation transfers full ownership rights of the final
                    product to the client. The client will receive all the final
                    core files of the logo/graphics/ui/website and will own 100%
                    of all rights to ownership of the said product.
                  </li>
                  <li>
                    <span className="font-w600">Exclusive Ownership: </span>The
                    client shall own the final product in its entirety, and all
                    ownership rights will belong to the client 100%.
                  </li>
                  <li>
                    <span className="font-w600">Usage Rights: </span>The client
                    is free to use the finalized product at their discretion.
                    They have the freedom to utilize the product for any purpose
                    they deem fit.
                  </li>
                  <li>
                    <span className="font-w600">
                      Revision Cycle Materials:{" "}
                    </span>
                    It's important to note that the ownership rights mentioned
                    above apply to the final product only. Any materials, media,
                    or other content generated during the revision cycles
                    leading up to the final product will not be owned by the
                    client.
                  </li>
                </ol>
                <h2 className="mt-5 mb-2">7. CONTENT CREATION & POPULATION</h2>
                <p className="mb-1 font-w600">
                  At TZ Creation, our standard policy does not include content
                  creation or population (e.g., text/images/videos) into the
                  website, unless specifically purchased as an additional
                  service. Please review the following information regarding our
                  Content Creation & Population policy:
                </p>
                <ul>
                  <li>
                    <span className="font-w600">Content Creation: </span>If the
                    client has purchased content creation services, TZ Creation
                    will create specific content/text for the client's website.
                    Our team of experts will work closely with the client to
                    develop engaging and tailored content that aligns with their
                    website's objectives and target audience.
                  </li>
                  <li>
                    <span className="font-w600">Image Population: </span>As part
                    of the content population process, TZ Creation will populate
                    the website with images. We will utilize stock imagery from
                    reputable stock photo websites to enhance the visual appeal
                    of the website during the development phase.
                  </li>
                  <li>
                    <span className="font-w600">Paid Asset Usage: </span>When it
                    comes to using paid images, icons, or fonts for design,
                    application, or website development, TZ Creation will seek
                    approval from the client. If the client approves the use of
                    certain paid assets, the associated costs for purchasing
                    those assets will be borne by the client.
                  </li>
                  <li>
                    <span className="font-w600">Similarities Disclaimer: </span>
                    TZ Creation cannot be held responsible for any similarities
                    between images used on the website and those used on other
                    websites. We strive to create unique and visually appealing
                    websites, but there may be instances where similarities
                    arise unintentionally.
                  </li>
                  <li>
                    <span className="font-w600">Product Population: </span>In
                    good faith, TZ Creation will populate the website with some
                    of the client's products and their relevant details.
                    However, it is the client's responsibility to provide the
                    necessary product images and details for all their products
                    and product variations. The client should provide the data
                    in a .CSV or .XLS file format.
                  </li>
                  <li>
                    <span className="font-w600">Data Population Charges: </span>
                    If the client provides a large quantity of images and
                    products for their website, additional data population
                    charges may apply. The specific details and pricing can be
                    discussed and agreed upon with the client.
                  </li>
                  <li>
                    <span className="font-w600">
                      Additional Editing Services:{" "}
                    </span>
                    Any kind of editing of images, such as resizing, cutting,
                    cropping, converting, or designing from scratch, will be
                    considered additional services and will incur additional
                    charges.
                  </li>
                </ul>
                <h2 className="mt-5 mb-2">
                  8. N.D.A. & REPUTATION MANAGEMENT POLICY
                </h2>
                <p className="mb-1 font-w600">
                  At TZ Creation, we prioritize the confidentiality and
                  reputation of our clients. Please review our N.D.A. &
                  Reputation Management policy outlined below:
                </p>
                <ul>
                  <li>
                    <span className="font-w600">
                      Confidentiality of Customer Information:
                    </span>{" "}
                    TZ Creation maintains a strict policy of not sharing any
                    customer information, including project details and contact
                    information, with any third parties. We are committed to
                    keeping all client information confidential and secure at
                    all times.
                  </li>
                  <li>
                    <span className="font-w600">
                      Post-Project Disparagement:
                    </span>{" "}
                    Upon successful completion of the project, both TZ Creation
                    and the client agree not to intentionally defame or
                    disparage each other with respect to matters arising after
                    project completion. We encourage a respectful and
                    professional relationship between all parties involved.
                  </li>
                  <li>
                    <span className="font-w600">Defamatory Comments:</span>{" "}
                    After project completion, both TZ Creation and the client
                    agree to refrain from making any defamatory comments, either
                    directly or indirectly, about the other party. TZ Creation
                    will take reasonable measures to ensure that its officers
                    and directors also abide by this policy.
                  </li>
                  <li>
                    <span className="font-w600">
                      Confidentiality During the Project:
                    </span>{" "}
                    All individuals involved in the project, including
                    employees, officers, consultants, agents, and any other
                    relevant parties, are expected to maintain strict
                    confidentiality. They must not disclose any project-related
                    information intentionally or unintentionally to unauthorized
                    individuals or entities.
                  </li>
                </ul>
                <h2 className="mt-5 mb-2">9. CUSTOMER SUPPORT</h2>
                <p>
                  Our Customer Support Team is available from Monday - Friday to
                  answer all of your concerns and queries over the email.
                </p>

                <h2 className="mt-5 mb-2">10. TECHNICAL SUPPORT POLICY</h2>
                <p className="mb-1 font-w600">
                  At TZ Creation, we strive to provide excellent IT services and
                  consultancy to our clients. While some of our packages may
                  include technical aspects such as website deployment, content
                  management systems (CMS), and email setup, it is important to
                  understand our technical support policy:
                </p>
                <h3 className="mt-5 mb-2">Website Deployment</h3>
                <ul>
                  <li>
                    TZ Creation will deploy the website on a hosting server
                    provided by the client or purchased through us.
                  </li>
                  <li>
                    If the hosting service is included in the package, it will
                    be provided as a free service. Otherwise, the client needs
                    to purchase hosting from a third-party service provider.
                  </li>
                  <li>
                    If the website is hosted on a third-party web server
                    purchased by the client, TZ Creation will not be responsible
                    for any server or hosting-related issues.
                  </li>
                  <li>
                    There is no ongoing Service Level Agreement (SLA) for
                    technical assistance or service outage, unless separately
                    purchased as an ongoing maintenance service.
                  </li>
                </ul>
                <h3 className="mt-5 mb-2">
                  Website Content Management System (CMS)
                </h3>
                <ul>
                  <li>
                    TZ Creation will provide the client with a user manual for
                    using the website CMS.
                  </li>
                  <li>
                    TZ Creation will not be responsible for training the client
                    in the usage of the CMS.
                  </li>
                </ul>
                <h3 className="mt-5 mb-2">Email Setup</h3>
                <ul>
                  <li>
                    TZ Creation will provide the necessary settings and
                    information for the client to install their emails.
                  </li>
                  <li>
                    TZ Creation will not be responsible for installing the
                    emails on the client's end.
                  </li>
                  <li>
                    TZ Creation will not provide technical support or assistance
                    in troubleshooting email usage issues as our emails are
                    hosted on open servers accessible by all service providers.
                  </li>
                </ul>
                <p className="mt-2">
                  Please note that TZ Creation is not responsible for any
                  failures on the client's end to execute, install, or learn the
                  usage of the aforementioned services. It is the client's
                  responsibility to handle these aspects and seek appropriate
                  technical assistance if required.
                </p>

                <h2 className="mt-5 mb-2">11. COMMUNICATION POLICY</h2>
                <p className="mb-1 font-w600">
                  At TZ Creation, we have established a communication policy to
                  ensure effective and official communication between our
                  clients and our team. Please take note of the following
                  guidelines:
                </p>
                <ul>
                  <li>
                    All feedback requests, revision requests, refund requests,
                    or any other instructions related to your order must be
                    submitted through the specified medium of communication by
                    TZ Creation in order to be considered official.
                  </li>
                  <li>
                    TZ Creation will not be held responsible for any
                    communication conducted via platforms other than pecified
                    medium of communication by TZ Creation or through phone
                    numbers not provided by us or listed on our official
                    website.
                  </li>
                  <li>
                    Official communication should be conducted through emails
                    sent to&nbsp;
                    <a href={`mailto:${getSiteEmail()}`}>{getSiteEmail()}</a>.
                    These emails will be considered as valid forms of
                    communication.
                  </li>
                  <li>
                    We cannot take responsibility for any damages, issues, or
                    delays arising from communication made through channels not
                    provided by TZ Creation.
                  </li>
                  <li>
                    We assume full responsibility for all information shared
                    through our official domains.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default TermsAndConditions;
